// Discovery is progressive enhancement over real, server-rendered cards.
// Shopify supplies sales order and online availability. Only product IDs and
// sampled page numbers persist for this tab; no prices or customer data do.
if (!customElements.get('bd-visit-discovery')) {
  const feedRequests = new Map();
  const collectionPools = new Map();
  let queued = false;
  let processing = false;
  const rows = () => [...document.querySelectorAll('bd-visit-discovery')];
  const popularPoolSize = row => Math.min(100, Math.max(20, Number(row.dataset.popularPoolSize) || 40));
  const shuffled = values => {
    const result = [...values];
    for (let index = result.length - 1; index > 0; index--) {
      const other = Math.floor(Math.random() * (index + 1));
      [result[index], result[other]] = [result[other], result[index]];
    }
    return result;
  };
  const readSaved = key => {
    try { const value = JSON.parse(sessionStorage.getItem(key)); return Array.isArray(value) ? value : []; }
    catch (_) { return []; }
  };
  const save = (key, value) => {
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* Optional tab stability. */ }
  };
  const candidates = container => new Map(
    [...container.querySelectorAll('template[data-bd-discovery-id]')]
      .filter(item => item.dataset.bdDiscoveryAvailable === 'true' && item.content.querySelector('li[data-bd-product-id]')?.dataset.bdProductId === item.dataset.bdDiscoveryId)
      .map(item => [item.dataset.bdDiscoveryId, item])
  );
  const merge = (...pools) => new Map(pools.flatMap(pool => [...pool]));

  async function fetchFeed(collectionURL, sort, page = 1) {
    const url = new URL(collectionURL, location.origin);
    if (url.origin !== location.origin) throw new Error('Discovery requires a local collection');
    url.search = '';
    url.searchParams.set('section_id', 'bd-discovery-feed');
    url.searchParams.set('sort_by', sort);
    url.searchParams.set('filter.v.availability', '1');
    url.searchParams.set('page', String(page));
    const key = url.href;
    if (!feedRequests.has(key)) feedRequests.set(key, (async () => {
      const abort = new AbortController();
      const timeout = setTimeout(() => abort.abort(), 5000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: abort.signal });
        if (!response.ok) throw new Error('Discovery feed unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const feed = parsed.querySelector('[data-bd-discovery-feed]');
        if (!feed || feed.dataset.sortOrder !== sort || Number(feed.dataset.page) !== page) throw new Error('Unexpected discovery feed');
        const pages = Math.max(1, Number(feed.dataset.pages) || 1);
        const total = Math.max(0, Number(feed.dataset.total) || 0);
        return { products: candidates(feed), pages, total, pageSize: 24 };
      } finally { clearTimeout(timeout); }
    })());
    return feedRequests.get(key);
  }

  async function loadPools(collectionURL, rankedLimit) {
    const poolKey = `${collectionURL}:${rankedLimit}`;
    if (collectionPools.has(poolKey)) return collectionPools.get(poolKey);
    const task = (async () => {
      let ranked = new Map();
      let catalog = new Map();
      let metadata;
      try {
        metadata = await fetchFeed(collectionURL, rankedLimit ? 'best-selling' : 'title-ascending');
        if (rankedLimit) {
          ranked = metadata.products;
          // Use the configured eligible best-seller pool. A bounded continuation
          // also handles stores where availability filtering is not enabled.
          const maxPages = Math.min(metadata.pages, Math.ceil(rankedLimit / metadata.pageSize) + 2);
          for (let page = 2; ranked.size < rankedLimit && page <= maxPages; page++) {
            try { ranked = merge(ranked, (await fetchFeed(collectionURL, 'best-selling', page)).products); }
            catch (_) { break; }
          }
          ranked = new Map([...ranked].slice(0, rankedLimit));
        }
      } catch (_) {
        // A sales-feed outage must not remove the working, stocked fallback.
        try { metadata = await fetchFeed(collectionURL, 'title-ascending'); catalog = metadata.products; }
        catch (_) { return { ranked, catalog }; }
      }
      const pageKey = `bd-discovery-pages-v1:${collectionURL}`;
      let pages = [...new Set(readSaved(pageKey))]
        .filter(page => Number.isInteger(page) && page >= 1 && page <= metadata.pages).slice(0, 2);
      const wanted = Math.min(2, metadata.pages);
      for (let attempt = 0; pages.length < wanted && attempt < 12; attempt++) {
        // Weight the shorter final page by its number of products.
        const item = Math.floor(Math.random() * Math.max(1, metadata.total));
        const page = Math.min(metadata.pages, Math.floor(item / metadata.pageSize) + 1);
        if (!pages.includes(page)) pages.push(page);
      }
      for (let page = 1; pages.length < wanted && page <= metadata.pages; page++) if (!pages.includes(page)) pages.push(page);
      save(pageKey, pages);
      const samples = await Promise.allSettled(pages.map(page => fetchFeed(collectionURL, 'title-ascending', page)));
      for (const sample of samples) if (sample.status === 'fulfilled') catalog = merge(catalog, sample.value.products);
      return { ranked, catalog };
    })();
    collectionPools.set(poolKey, task);
    return task;
  }

  function select(pool, count, previous, used, elsewhere, preferGroups) {
    const selected = [];
    const groups = new Set();
    const order = [...new Set([...previous.filter(id => typeof id === 'string' && pool.has(id)), ...shuffled(pool.keys())])];
    const choose = (avoidUsed, avoidElsewhere, varied = false) => {
      for (const id of order) {
        if (selected.length >= count) break;
        if (selected.includes(id) || (avoidUsed && used.has(id)) || (avoidElsewhere && elsewhere.has(id))) continue;
        const group = pool.get(id).dataset.bdDiscoveryGroup;
        if (varied && (!group || groups.has(group))) continue;
        selected.push(id);
        if (group) groups.add(group);
      }
    };
    if (preferGroups) choose(true, true, true);
    choose(true, true);
    choose(true, false); // Keep four before excluding every other homepage feature.
    choose(false, false); // A genuinely small catalog can repeat across rows, never within one.
    return selected;
  }

  async function updateRows() {
    queued = false;
    if (processing) return;
    processing = true;
    try {
      const activeRows = rows();
      const pending = activeRows.filter(row => row.dataset.ready !== 'true');
      const dynamic = pending.filter(row => row.querySelector('.bd-products__grid') && ['popular', 'catalog', 'red_tag'].includes(row.dataset.mode));
      const sources = [...new Set(dynamic.map(row => row.dataset.collectionUrl))];
      const loaded = new Map(await Promise.all(sources.map(async source => {
        const rankedLimit = Math.max(0, ...dynamic
          .filter(row => row.dataset.collectionUrl === source && row.dataset.mode === 'popular')
          .map(popularPoolSize));
        return [source, await loadPools(source, rankedLimit)];
      })));
      const elsewhere = new Set(
        [...document.querySelectorAll('[data-bd-product-id]')]
          .filter(card => !card.closest('bd-visit-discovery'))
          .map(card => card.dataset.bdProductId)
      );
      const used = new Set();
      for (const row of activeRows) {
        if (!row.isConnected) continue;
        const grid = row.querySelector('.bd-products__grid');
        if (!grid) { row.dataset.ready = 'true'; continue; }
        if (row.dataset.ready === 'true') {
          for (const card of grid.children) used.add(card.dataset.bdProductId);
          continue;
        }
        const fallback = candidates(row);
        const count = Math.min(24, Math.max(1, Number(row.dataset.count) || 4));
        const mode = row.dataset.mode || 'visit';
        const remote = loaded.get(row.dataset.collectionUrl);
        let pool = fallback;
        let source = 'server-fallback';
        if (mode === 'popular' && remote?.ranked.size) {
          pool = new Map([...remote.ranked].slice(0, popularPoolSize(row)));
          source = 'shopify-best-selling';
          if (pool.size < count) { pool = merge(pool, fallback, remote.catalog); source = 'best-selling-with-backfill'; }
        } else if (['catalog', 'red_tag'].includes(mode) && remote?.catalog.size) {
          pool = remote.catalog;
          source = mode === 'red_tag' ? 'red-tag-sample' : 'catalog-sample';
          if ([...pool.keys()].filter(id => !used.has(id)).length < count) pool = merge(pool, fallback, remote.ranked);
        }
        const key = `bd-discovery-v4:${row.dataset.sectionId}:${mode}:${row.dataset.collectionUrl || 'selected'}:${mode === 'popular' ? popularPoolSize(row) : 'sample'}`;
        // Popular means a literal sample of the ranked pool. Excluding software
        // already featured elsewhere would silently bias it toward consumables.
        const selected = select(pool, count, readSaved(key), used, mode === 'popular' ? new Set() : elsewhere, mode === 'visit');
        const cards = document.createDocumentFragment();
        for (const id of selected) {
          cards.append(document.importNode(pool.get(id).content, true));
          used.add(id);
        }
        grid.replaceChildren(cards);
        row.hidden = selected.length === 0;
        row.dataset.ready = 'true';
        row.dataset.source = source;
        save(key, selected);
      }
    } finally {
      processing = false;
      if (rows().some(row => row.dataset.ready !== 'true')) schedule();
    }
  }

  function schedule() {
    if (queued || processing) return;
    queued = true;
    queueMicrotask(updateRows);
  }
  let observer;
  function prepare(row) {
    if (!row.isConnected) return;
    if ('IntersectionObserver' in window && !row.hidden) {
      observer ||= new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); schedule(); }
      }, { rootMargin: '1200px 0px' });
      observer.observe(row);
    } else schedule();
  }
  customElements.define('bd-visit-discovery', class extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => prepare(this), { once: true });
      else queueMicrotask(() => prepare(this));
    }
    disconnectedCallback() { observer?.unobserve(this); }
  });
}
